import {
  AlertBar,
  Anchor,
  ArrowLeftIcon,
  Button,
  ExclamationTriangleIcon,
  Flex,
  Form,
  Text,
  TextInput,
  Title,
  fieldPath,
} from 'libs/uikit/lib';
import { useAsyncEvent } from 'libs/uikit/lib/Hooks/useAsyncEvent';
import { errorText } from 'libs/uikit/lib/Text';
import React from 'react';
import { Link } from 'react-router-dom';
import uri from 'urijs';
import { z } from 'zod';
import { appconfig } from '../../config/appconfig';

export interface Props {
  onComplete: () => void;
}

const schema = z.object({
  email: z.string().email(),
});

export function ForgotPasswordForm(props: Props) {
  const onSubmit = useAsyncEvent(async (data: z.infer<typeof schema>) => {
    const response = await fetch(uri(appconfig.webapi.endpoint).segment('/api/User/ForgotPassword').valueOf(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        toEmailAddress: data.email,
      }),
    });

    if (response.ok) {
      props.onComplete();
    } else {
      const responseError = await response.json();
      throw new Error(responseError.errorDescription ?? 'An unexpected error occurred.');
    }
  });

  return (
    <Flex gap={2} align="stretch">
      <Title size="h1" bold>
        Forgotten your password?
      </Title>

      <Text>
        Enter your email address below and we will send you an email with instructions to reset your password.
      </Text>

      <Form schema={schema} onSubmit={onSubmit.callback}>
        <TextInput name={fieldPath(schema, 'email')} label="Email address" placeholder="person@example.com" />

        {!!onSubmit.error && (
          <AlertBar
            intent="warning"
            title={errorText(onSubmit.error)}
            icon={ExclamationTriangleIcon}
            onDismiss={() => onSubmit.dismissError()}
          />
        )}

        <Button type="submit" primary scale={2}>
          Reset password
        </Button>
      </Form>

      <Anchor as={Link} to="/account/login" noUnderline>
        <Button secondary variant="text" iconLeft={ArrowLeftIcon} scale={1}>
          Back to log in
        </Button>
      </Anchor>
    </Flex>
  );
}
